import { useState, useEffect, useRef } from 'react';
import { useBusinessSettings } from '../context/BusinessSettingsContext';

const CAT_COLORS = {
  Noodles:  'linear-gradient(135deg,#fde68a,#fbbf24)',
  Curries:  'linear-gradient(135deg,#fed7aa,#fb923c)',
  Soups:    'linear-gradient(135deg,#bbf7d0,#4ade80)',
  Starters: 'linear-gradient(135deg,#bfdbfe,#60a5fa)',
  Desserts: 'linear-gradient(135deg,#fde8d8,#f9a8d4)',
  Rice:     'linear-gradient(135deg,#e0f2fe,#38bdf8)',
  Drinks:   'linear-gradient(135deg,#ffe4e6,#fb7185)',
  default:  'linear-gradient(135deg,#e2e8f0,#94a3b8)',
};

const SPICE = ['', '🌶', '🌶🌶'];

function MenuCard({ item, onAdd }) {
  const { formatPrice } = useBusinessSettings();
  const [hovered, setHovered] = useState(false);
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    if (item.soldOut) return;
    onAdd(item);
    setAdded(true);
    setTimeout(() => setAdded(false), 800);
  };

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'white',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        boxShadow: hovered ? 'var(--shadow-hover)' : 'var(--shadow-card)',
        transform: hovered ? 'translateY(-6px) scale(1.01)' : 'none',
        transition: 'all .28s cubic-bezier(.34,1.56,.64,1)',
        border: '1px solid var(--border)',
        opacity: item.soldOut ? 0.65 : 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image / emoji area */}
      <div style={{ position: 'relative', overflow: 'hidden', height: 190 }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover',
              transform: hovered ? 'scale(1.07)' : 'scale(1)',
              transition: 'transform .4s ease',
            }}
          />
        ) : (
          <div
            style={{
              width: '100%', height: '100%',
              background: CAT_COLORS[item.category] || CAT_COLORS.default,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 80,
              transform: hovered ? 'scale(1.06)' : 'scale(1)',
              transition: 'transform .4s ease',
            }}
          >
            {item.emoji}
          </div>
        )}

        {/* Tags */}
        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {item.tags.includes('bestseller') && (
            <span style={{ background: '#fff8e6', color: '#b07e22', padding: '3px 10px', borderRadius: 999, fontSize: 'var(--text-label-medium-size)', lineHeight: 'var(--text-label-medium-line)', fontWeight: 700, textTransform: 'uppercase' }}>
              ⭐ Best Seller
            </span>
          )}
          {item.tags.includes('spicy') && (
            <span style={{ background: '#fff0f0', color: '#c0392b', padding: '3px 10px', borderRadius: 999, fontSize: 'var(--text-label-medium-size)', lineHeight: 'var(--text-label-medium-line)', fontWeight: 700, textTransform: 'uppercase' }}>
              🌶 Spicy
            </span>
          )}
          {item.tags.includes('veg') && (
            <span style={{ background: '#f0f8f0', color: '#27ae60', padding: '3px 10px', borderRadius: 999, fontSize: 'var(--text-label-medium-size)', lineHeight: 'var(--text-label-medium-line)', fontWeight: 700, textTransform: 'uppercase' }}>
              🥦 Veg
            </span>
          )}
          {item.tags.includes('new') && (
            <span style={{ background: '#e8f4fb', color: '#1a7a8a', padding: '3px 10px', borderRadius: 999, fontSize: 'var(--text-label-medium-size)', lineHeight: 'var(--text-label-medium-line)', fontWeight: 700, textTransform: 'uppercase' }}>
              New
            </span>
          )}
        </div>

        {/* Prep time badge */}
        {item.prepMinutes && !item.soldOut && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(13,30,34,0.75)', backdropFilter: 'blur(6px)',
            color: 'white', fontSize: 'var(--text-label-medium-size)', lineHeight: 'var(--text-label-medium-line)', fontWeight: 600,
            padding: '3px 9px', borderRadius: 999,
          }}>
            ⏱ {item.prepMinutes} min
          </div>
        )}

        {/* Sold out overlay */}
        {item.soldOut && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 700, fontSize: '.9rem', letterSpacing: '.1em',
          }}>
            SOLD OUT TODAY
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '1.1rem 1.1rem 1.25rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', fontWeight: 700, color: 'var(--dark)', marginBottom: '.35rem' }}>
          {item.name}
          {item.spiceLevel > 0 && (
            <span style={{ marginLeft: 6, fontSize: '.85rem' }}>{SPICE[item.spiceLevel]}</span>
          )}
        </div>
        <div style={{
          color: 'var(--gray)', fontSize: '.83rem', lineHeight: 1.55,
          marginBottom: '1rem', flex: 1,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {item.desc}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', fontWeight: 700, color: 'var(--teal-dark)' }}>
              {formatPrice(item.price)}
            </span>
            <span style={{ fontSize: '.73rem', color: 'var(--gray)', marginLeft: 4 }}>/serving</span>
          </div>
          {!item.soldOut ? (
            <button
              onClick={handleAdd}
              aria-label={`Add ${item.name} to order`}
              style={{
                width: 38, height: 38,
                background: added ? 'var(--gold)' : 'var(--teal)',
                border: 'none',
                borderRadius: '50%',
                color: 'white',
                fontSize: '1.3rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all .22s cubic-bezier(.34,1.56,.64,1)',
                transform: added ? 'scale(1.35)' : 'scale(1)',
                lineHeight: 1,
              }}
            >
              {added ? '✓' : '+'}
            </button>
          ) : (
            <span style={{ color: 'var(--red)', fontSize: '.76rem', fontWeight: 600 }}>Unavailable</span>
          )}
        </div>
      </div>
    </article>
  );
}

export default function MenuSection({ menuItems, onAddToCart }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const filtered = activeCategory === 'All'
    ? menuItems
    : menuItems.filter((i) => i.category === activeCategory);

  // Build categories dynamically from currently available menu items.
  const dynamicCategories = Array.from(
    new Set(
      menuItems
        .map((i) => String(i.category || '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
  const availableCats = ['All', ...dynamicCategories];

  return (
    <section className="section" id="menu" style={{ background: 'var(--white)' }}>
      <div className="section-inner">
        <div className="reveal" ref={sectionRef}>
          <span className="section-label">Our Menu</span>
          <h2 className="section-title">Authentic Thai Cuisine,<br />Made with Love</h2>
          <p className="section-subtitle">
            Every dish prepared fresh using traditional recipes and the finest ingredients.
            Order online, pay and collect ready at the kiosk.
          </p>
        </div>

        {/* Category tabs */}
        <div
          style={{
            display: 'flex',
            gap: '.45rem',
            flexWrap: 'wrap',
            margin: '2rem 0',
          }}
          role="tablist"
          aria-label="Menu categories"
        >
          {availableCats.map((cat) => (
            <button
              key={cat}
              role="tab"
              aria-selected={cat === activeCategory}
              onClick={() => setActiveCategory(cat)}
              style={{
                background: cat === activeCategory ? 'var(--teal)' : 'none',
                border: `1.5px solid ${cat === activeCategory ? 'var(--teal)' : 'var(--border)'}`,
                color: cat === activeCategory ? 'white' : 'var(--gray)',
                padding: '.5rem 1.2rem',
                borderRadius: 'var(--radius-pill)',
                fontFamily: 'var(--font-body)',
                fontSize: '.875rem',
                fontWeight: 500,
                transition: 'all .18s',
              }}
              onMouseEnter={(e) => {
                if (cat !== activeCategory) {
                  e.currentTarget.style.borderColor = 'var(--teal)';
                  e.currentTarget.style.color = 'var(--teal)';
                }
              }}
              onMouseLeave={(e) => {
                if (cat !== activeCategory) {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.color = 'var(--gray)';
                }
              }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray)' }}>
            No items available in this category today.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
              gap: '1.6rem',
            }}
          >
            {filtered.map((item) => (
              <MenuCard key={item.id} item={item} onAdd={onAddToCart} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
